import React, { useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Camera, Wand2, Check, RefreshCw, Upload, Plus, Trash2, Search, UserPlus, BookOpen } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { callClaude, parseJsonResponse } from '../lib/claude'
import { uploadPrivate } from '../lib/storage'

const GARMENT_TYPES = ['suit','jacket','trouser','shirt','tuxedo','coat','blazer','shorts','other']
const CURRENCIES = ['THB','USD','GBP','EUR','MYR','AUD','CAD','SGD','NOK']
const HISTORICAL_STATUSES = [
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'order_confirmed', label: 'Not sure / still open' },
]
const OLD_ORDER_DAYS = 60
const LAST_BOOK_KEY = 'jt_last_source_book'

const MEASUREMENT_FIELDS = [
  { key:'chest', label:'Chest' }, { key:'waist', label:'Waist' }, { key:'hips', label:'Hips' },
  { key:'shoulders', label:'Shoulders' }, { key:'back_length', label:'Back Length' },
  { key:'sleeve_right', label:'Sleeve (R)' }, { key:'sleeve_left', label:'Sleeve (L)' },
  { key:'bicep', label:'Bicep' }, { key:'wrist', label:'Wrist' }, { key:'neck', label:'Neck' },
  { key:'trouser_waist', label:'Trouser Waist' }, { key:'seat', label:'Seat' },
  { key:'thigh', label:'Thigh' }, { key:'inseam', label:'Inseam' }, { key:'outseam', label:'Outseam' },
  { key:'bottom_width', label:'Bottom Width' }, { key:'rise_front', label:'Rise (Front)' },
  { key:'rise_back', label:'Rise (Back)' }, { key:'collar', label:'Collar' },
  { key:'shirt_chest', label:'Shirt Chest' }, { key:'shirt_waist', label:'Shirt Waist' },
  { key:'shirt_length', label:'Shirt Length' }, { key:'shirt_sleeve', label:'Shirt Sleeve' },
  { key:'height', label:'Height' },
]

const today = () => new Date().toISOString().split('T')[0]
const isValidDate = s => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(new Date(s).getTime())
const daysAgo = s => (Date.now() - new Date(s).getTime()) / 86400000

function readLastBook() { try { return localStorage.getItem(LAST_BOOK_KEY) || '' } catch { return '' } }
function saveLastBook(v) { try { localStorage.setItem(LAST_BOOK_KEY, v) } catch {} }

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      const canvas = document.createElement('canvas')
      const maxSize = 1600
      let { width, height } = img
      if (width > maxSize || height > maxSize) {
        if (width > height) { height = Math.round(height * maxSize / width); width = maxSize }
        else { width = Math.round(width * maxSize / height); height = maxSize }
      }
      canvas.width = width; canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, width, height)
      ctx.drawImage(img, 0, 0, width, height)
      URL.revokeObjectURL(url)
      resolve(canvas.toDataURL('image/jpeg', 0.92).split(',')[1])
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not load image')) }
    img.src = url
  })
}

function base64ToBlob(b64, type = 'image/jpeg') {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes], { type })
}

function NewClientPopup({ clientData, onConfirm, onCancel }) {
  const [form, setForm] = useState({
    first_name: clientData.first_name || '',
    last_name: clientData.last_name || '',
    phone: clientData.phone || '',
    whatsapp: clientData.whatsapp || '',
    email: clientData.email || '',
    country: clientData.country || '',
    city: clientData.city || '',
    notes: clientData.notes || '',
  })
  const [saving, setSaving] = useState(false)
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200, display:'flex', alignItems:'flex-end', justifyContent:'center' }}>
      <div style={{ background:'var(--white)', borderRadius:'20px 20px 0 0', padding:'24px 20px 40px', width:'100%', maxWidth:'500px', maxHeight:'85vh', overflowY:'auto' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div>
            <h2 style={{ fontSize:'18px', fontWeight:'700' }}>New Client</h2>
            <p style={{ fontSize:'13px', color:'var(--gray-500)', marginTop:'2px' }}>Review details before saving</p>
          </div>
          <button onClick={onCancel} style={{ background:'none', border:'none', fontSize:'22px', color:'var(--gray-400)', cursor:'pointer' }}>×</button>
        </div>
        <div style={{ background:'var(--amber-light)', border:'1px solid var(--amber)', borderRadius:'8px', padding:'10px 12px', fontSize:'12px', color:'#92400E', marginBottom:'16px' }}>
          No matching client was confirmed. Check the spelling, then save to add them.
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">First Name</label><input className="form-input" value={form.first_name} onChange={e => setForm(p=>({...p,first_name:e.target.value}))}/></div>
          <div className="form-group"><label className="form-label">Last Name</label><input className="form-input" value={form.last_name} onChange={e => setForm(p=>({...p,last_name:e.target.value}))}/></div>
        </div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Phone</label><input className="form-input" type="tel" value={form.phone} onChange={e => setForm(p=>({...p,phone:e.target.value}))} placeholder="+61 xxx xxx xxx"/></div>
          <div className="form-group"><label className="form-label">WhatsApp</label><input className="form-input" type="tel" value={form.whatsapp} onChange={e => setForm(p=>({...p,whatsapp:e.target.value}))} placeholder="If different"/></div>
        </div>
        <div className="form-group"><label className="form-label">Email <span>(optional)</span></label><input className="form-input" type="email" value={form.email} onChange={e => setForm(p=>({...p,email:e.target.value}))}/></div>
        <div className="form-row">
          <div className="form-group"><label className="form-label">Country</label><input className="form-input" value={form.country} onChange={e => setForm(p=>({...p,country:e.target.value}))}/></div>
          <div className="form-group"><label className="form-label">City</label><input className="form-input" value={form.city} onChange={e => setForm(p=>({...p,city:e.target.value}))}/></div>
        </div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-textarea" rows={2} value={form.notes} onChange={e => setForm(p=>({...p,notes:e.target.value}))}/></div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'8px' }}>
          <button className="btn btn-outline" onClick={onCancel}>Skip for now</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => { setSaving(true); await onConfirm(form); setSaving(false) }}>
            {saving ? 'Saving…' : '✓ Save Client'}
          </button>
        </div>
      </div>
    </div>
  )
}

function CandidateRow({ c, selected, onPick }) {
  const name = [c.first_name, c.last_name].filter(Boolean).join(' ') || 'No name'
  const strong = c.reasons?.includes('phone') || c.reasons?.includes('email')
  return (
    <button type="button" onClick={() => onPick(c)} style={{
      display:'flex', width:'100%', textAlign:'left', gap:'10px', alignItems:'flex-start',
      padding:'10px 12px', marginBottom:'8px', borderRadius:'10px', cursor:'pointer',
      border: selected ? '2px solid var(--green)' : '1.5px solid var(--gray-200)',
      background: selected ? '#f0fff4' : 'var(--white)',
    }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontWeight:'600', fontSize:'14px' }}>{name} {selected && <Check size={14} style={{ color:'var(--green)', verticalAlign:'middle' }}/>}</div>
        <div style={{ fontSize:'12px', color:'var(--gray-500)', marginTop:'2px' }}>
          {[c.phone || c.whatsapp, c.email, [c.city, c.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'No contact details'}
        </div>
        <div style={{ display:'flex', gap:'4px', flexWrap:'wrap', marginTop:'5px' }}>
          {(c.reasons || []).map(r => <span key={r} className={`badge ${r === 'phone' || r === 'email' ? 'badge-green' : 'badge-gray'}`} style={{ fontSize:'10px' }}>{r} match</span>)}
          {c.order_count > 0 && <span className="badge badge-gold" style={{ fontSize:'10px' }}>{c.order_count} order{c.order_count > 1 ? 's' : ''}</span>}
        </div>
      </div>
      <span style={{ fontSize:'12px', fontWeight:'700', color: strong ? 'var(--green)' : 'var(--gray-400)' }}>{c.score}</span>
    </button>
  )
}

export default function ScanOrder() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const cameraRef = useRef()
  const uploadRef = useRef()

  const [image, setImage] = useState(null)
  const [imageBase64, setImageBase64] = useState(null)
  const [scanning, setScanning] = useState(false)
  const [scanned, setScanned] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  // What the sheet says about the client
  const [sheet, setSheet] = useState({ name:'', phone:'', whatsapp:'', email:'', city:'', country:'' })
  // Matching
  const [candidates, setCandidates] = useState([])
  const [matching, setMatching] = useState(false)
  const [lookedUp, setLookedUp] = useState(false)
  const [selectedClient, setSelectedClient] = useState(null)
  const [showNewClientPopup, setShowNewClientPopup] = useState(false)

  // Order type: new today, or an old order copied from the books
  const [historical, setHistorical] = useState(false)
  const [orderDate, setOrderDate] = useState(today())
  const [status, setStatus] = useState('measurements_taken')
  const [legacyNumber, setLegacyNumber] = useState('')
  const [linkedNumbers, setLinkedNumbers] = useState('')
  const [sourceBook, setSourceBook] = useState(readLastBook())
  const [sourcePage, setSourcePage] = useState('')

  const [trunkShowCity, setTrunkShowCity] = useState('')
  const [notes, setNotes] = useState('')
  const [lineItems, setLineItems] = useState([
    { garment_type:'suit', qty:1, description:'', style_notes:'', fabric_notes:'', price:'', currency:'THB' }
  ])
  const [measurements, setMeasurements] = useState({})

  function setSheetField(k, v) { setSheet(p => ({ ...p, [k]: v })) }

  function setOrderType(isOld) {
    setHistorical(isOld)
    if (isOld) { if (status === 'measurements_taken') setStatus('delivered') }
    else { setStatus('measurements_taken') }
  }

  async function handleImageSelect(e) {
    const file = e.target.files[0]
    if (!file) return
    setError(''); setScanned(false)
    try {
      setImage(URL.createObjectURL(file))
      setImageBase64(await fileToBase64(file))
    } catch { setError('Could not load image. Try another photo.') }
  }

  async function findMatches(s = sheet) {
    if (!s.name && !s.phone && !s.whatsapp && !s.email) { setCandidates([]); setLookedUp(true); return [] }
    setMatching(true)
    const { data, error: rpcErr } = await supabase.rpc('match_clients', {
      p_name: s.name || null,
      p_phone: s.phone || s.whatsapp || null,
      p_email: s.email || null,
      p_city: s.city || null,
      p_limit: 5,
    })
    setMatching(false)
    setLookedUp(true)
    if (rpcErr) {
      setError('Client matching is not set up yet. Run migration 001 in Supabase. (' + rpcErr.message + ')')
      return []
    }
    let list = data || []
    // A second lookup by WhatsApp when it differs from the phone
    if (s.whatsapp && s.phone && s.whatsapp !== s.phone) {
      const { data: more } = await supabase.rpc('match_clients', { p_name: s.name || null, p_phone: s.whatsapp, p_email: s.email || null, p_city: s.city || null, p_limit: 5 })
      const seen = new Set(list.map(c => c.id))
      list = [...list, ...(more || []).filter(c => !seen.has(c.id))].sort((a, b) => b.score - a.score).slice(0, 5)
    }
    setCandidates(list)
    return list
  }

  // Link automatically only on a phone or email match that clearly beats the rest.
  function autoPick(list) {
    const [top, second] = list
    if (!top) return null
    const strong = top.reasons?.includes('phone') || top.reasons?.includes('email')
    if (strong && (!second || second.score <= top.score - 20)) return top
    return null
  }

  async function scanWithClaude() {
    if (!imageBase64) return
    setScanning(true); setError('')
    try {
      const data = await callClaude({
        model: 'claude-opus-4-5',
        max_tokens: 2500,
        messages: [{
          role: 'user',
          content: [
            { type:'image', source:{ type:'base64', media_type:'image/jpeg', data:imageBase64 } },
            { type:'text', text:`You are an expert tailor's assistant. Read this handwritten order sheet carefully.

Extract ALL information. Key things to find:
- Client name, phone numbers (including WhatsApp — look for "+", country codes, "WhatsApp:" labels) and email
- The date written on the sheet
- The order or bill number written on this sheet
- Any OTHER order numbers this page refers to (e.g. "see 1234", "same as #1234", "repeat 1234", "copy of 1234")
- MULTIPLE garment types — list each separately (e.g. pants AND shirts = 2 line items)
- Prices per item or totals
- Measurements — use position, abbreviations, body diagrams to identify each value
- City or location
- Fabric notes

Return ONLY valid JSON:
{
  "client_name": "",
  "client_phone": "",
  "client_whatsapp": "",
  "client_email": "",
  "client_country": "",
  "client_city": "",
  "trunk_show_city": "",
  "order_date": null,
  "legacy_order_number": null,
  "linked_order_numbers": [],
  "notes": "",
  "line_items": [
    {
      "garment_type": "trouser",
      "qty": 1,
      "description": "",
      "style_notes": "",
      "fabric_notes": "",
      "price": null,
      "currency": "THB"
    }
  ],
  "measurements": {
    "chest":null,"waist":null,"hips":null,"shoulders":null,"back_length":null,
    "sleeve_right":null,"sleeve_left":null,"bicep":null,"wrist":null,"neck":null,
    "trouser_waist":null,"seat":null,"thigh":null,"inseam":null,"outseam":null,
    "bottom_width":null,"rise_front":null,"rise_back":null,
    "collar":null,"shirt_chest":null,"shirt_waist":null,"shirt_length":null,"shirt_sleeve":null,
    "height":null
  }
}

Rules:
- line_items is an ARRAY — one entry per garment TYPE (separate trouser entry, separate shirt entry, etc.)
- Valid garment_type: suit, jacket, trouser, shirt, tuxedo, coat, blazer, shorts, other
- order_date: "YYYY-MM-DD". If only month and year are written, use the 1st of the month. If the date is unreadable, use null
- legacy_order_number: the number as written, digits and letters only, or null
- linked_order_numbers: other order numbers referenced on this page, as written; [] if none
- Keep descriptions exactly as written — do not rename or translate
- If cm detected, convert measurements to inches (divide by 2.54)
- Return ONLY the JSON` }
          ]
        }]
      })
      const parsed = parseJsonResponse(data)

      const s = {
        name: parsed.client_name || '',
        phone: parsed.client_phone || '',
        whatsapp: parsed.client_whatsapp || '',
        email: parsed.client_email || '',
        city: parsed.client_city || '',
        country: parsed.client_country || '',
      }
      setSheet(s)
      setTrunkShowCity(parsed.trunk_show_city || '')
      setNotes(parsed.notes || '')
      setLegacyNumber(parsed.legacy_order_number ? String(parsed.legacy_order_number) : '')
      setLinkedNumbers((parsed.linked_order_numbers || []).map(String).join(', '))
      if (isValidDate(parsed.order_date)) {
        setOrderDate(parsed.order_date)
        if (daysAgo(parsed.order_date) > OLD_ORDER_DAYS) setOrderType(true)
      }

      if (parsed.line_items?.length > 0) {
        setLineItems(parsed.line_items.map(item => ({
          garment_type: GARMENT_TYPES.includes(item.garment_type) ? item.garment_type : 'other',
          qty: item.qty || 1,
          description: item.description || '',
          style_notes: item.style_notes || '',
          fabric_notes: item.fabric_notes || '',
          price: item.price != null ? String(item.price) : '',
          currency: item.currency || 'THB',
        })))
      }

      const m = {}
      Object.entries(parsed.measurements || {}).forEach(([k,v]) => {
        if (v != null && v !== '' && !isNaN(parseFloat(v))) m[k] = v
      })
      setMeasurements(m)

      setSelectedClient(null)
      const list = await findMatches(s)
      const pick = autoPick(list)
      if (pick) setSelectedClient(pick)
      else if (list.length === 0 && s.name) setShowNewClientPopup(true)

      setScanned(true)
    } catch(err) {
      setError('Could not process image. Error: ' + err.message)
    }
    setScanning(false)
  }

  async function handleConfirmNewClient(form) {
    const { data: c, error: insErr } = await supabase.from('clients').insert({
      first_name: form.first_name || null, last_name: form.last_name || null,
      phone: form.phone||null, whatsapp: form.whatsapp||null,
      email: form.email ? form.email.trim().toLowerCase() : null, country: form.country||null,
      city: form.city||null, notes: form.notes||null,
      contact_type: 'Past Client', added_by: profile?.id,
    }).select().single()
    if (insErr) { alert('Could not save client: ' + insErr.message); return }
    setSelectedClient({ ...c, reasons: ['new'], score: 100, order_count: 0 })
    setCandidates(p => [{ ...c, reasons: ['new'], score: 100, order_count: 0 }, ...p])
    setShowNewClientPopup(false)
  }

  function setItem(i, k, v) { setLineItems(p => p.map((l,idx) => idx===i ? {...l,[k]:v} : l)) }
  function addItem() { setLineItems(p => [...p, { garment_type:'shirt', qty:1, description:'', style_notes:'', fabric_notes:'', price:'', currency:'THB' }]) }
  function removeItem(i) { setLineItems(p => p.filter((_,idx) => idx!==i)) }
  function setMeasure(k, v) { setMeasurements(p => ({...p,[k]:v})) }

  function orderLabel() {
    if (lineItems.length === 1) return lineItems[0].garment_type
    const types = lineItems.map(l => l.garment_type.charAt(0).toUpperCase() + l.garment_type.slice(1)).join(', ')
    return `${lineItems.length} items — ${types}`
  }

  // Adds phone/email/city from the sheet to a matched client, only where the client has nothing yet.
  async function fillMissingContactFields(client) {
    if (!client?.id) return
    const patch = {}
    const pairs = [['phone', sheet.phone], ['whatsapp', sheet.whatsapp], ['email', sheet.email && sheet.email.trim().toLowerCase()], ['city', sheet.city], ['country', sheet.country]]
    pairs.forEach(([k, v]) => { if (v && !client[k]) patch[k] = v })
    if (Object.keys(patch).length) await supabase.from('clients').update(patch).eq('id', client.id)
  }

  async function handleSave() {
    if (!isValidDate(orderDate)) { alert('Please enter a valid order date.'); return }
    const clientId = selectedClient?.id || null
    if (!clientId && !window.confirm('No client is linked to this order. Save it anyway? You can link the client later.')) return

    setSaving(true)
    try {
      // 1. Keep the photo of the sheet (private bucket)
      let scanRef = null
      if (imageBase64) {
        scanRef = await uploadPrivate('order-scans', `scans/${Date.now()}.jpg`, base64ToBlob(imageBase64), 'image/jpeg')
      }

      await fillMissingContactFields(selectedClient)

      // 2. The order itself
      const primaryType = lineItems.length === 1 ? lineItems[0].garment_type : 'other'
      const totalPrice = lineItems.some(l => l.price)
        ? lineItems.reduce((sum, l) => sum + (Number(l.price||0) * Number(l.qty||1)), 0)
        : null
      const linked = linkedNumbers.split(/[,;\s]+/).map(s => s.replace(/^#/, '').trim()).filter(Boolean)

      const { data: order, error: orderErr } = await supabase.from('orders').insert({
        garment_type: primaryType,
        description: orderLabel(),
        line_items: lineItems,
        quantity: lineItems.reduce((sum, l) => sum + Number(l.qty||1), 0),
        trunk_show_city: trunkShowCity || null,
        notes: notes || null,
        handwritten_scan_url: scanRef,
        client_id: clientId,
        status,
        order_date: orderDate,
        price: totalPrice,
        currency: lineItems[0]?.currency || 'THB',
        taken_by: profile?.id,
        is_historical: historical,
        legacy_order_number: legacyNumber.trim() || null,
        linked_order_numbers: linked,
        source_book: historical ? (sourceBook.trim() || null) : null,
        source_page: historical ? (sourcePage.trim() || null) : null,
      }).select().single()
      if (orderErr) throw new Error('Error saving order: ' + orderErr.message)
      if (historical && sourceBook.trim()) saveLastBook(sourceBook.trim())

      // 3. Measurements: always a NEW dated set linked to this order. Never overwrite.
      const values = {}
      Object.entries(measurements).forEach(([k, v]) => { const n = parseFloat(v); if (!isNaN(n)) values[k] = n })
      if (Object.keys(values).length) {
        const { data: m, error: mErr } = await supabase.from('measurements').insert({
          client_id: clientId,
          order_id: order.id,
          garment_type: 'suit',
          unit: 'in',
          measured_at: `${orderDate}T12:00:00Z`,
          measured_by: profile?.id,
          source: historical ? 'ledger' : 'scan',
          ...values,
        }).select('id').single()
        if (mErr) alert('Order saved, but measurements failed: ' + mErr.message)
        else await supabase.from('orders').update({ measurement_id: m.id }).eq('id', order.id)
      }

      navigate(`/orders/${order.id}`)
    } catch (err) {
      alert(err.message || 'Could not save')
    }
    setSaving(false)
  }

  const segBtn = active => ({
    flex:1, padding:'10px', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600',
    border: active ? '2px solid var(--brown-dark)' : '1.5px solid var(--gray-200)',
    background: active ? 'var(--brown-dark)' : 'var(--white)', color: active ? 'var(--gold)' : 'var(--gray-600)',
  })

  return (
    <div>
      {showNewClientPopup && (
        <NewClientPopup
          clientData={{
            first_name: sheet.name.trim().split(/\s+/)[0] || '',
            last_name: sheet.name.trim().split(/\s+/).slice(1).join(' '),
            phone: sheet.phone, whatsapp: sheet.whatsapp, email: sheet.email,
            country: sheet.country, city: sheet.city,
          }}
          onConfirm={handleConfirmNewClient}
          onCancel={() => setShowNewClientPopup(false)} />
      )}

      <div className="page-header">
        <button className="page-header-back" onClick={() => navigate(-1)}>←</button>
        <h1 className="page-title">Scan Order Sheet</h1>
      </div>

      {/* Photo */}
      <div className="card" style={{ marginBottom:'12px' }}>
        <div className="form-section-title">Photo of Handwritten Sheet</div>
        {image ? (
          <div style={{ position:'relative', marginBottom:'10px' }}>
            <img src={image} alt="scan" style={{ width:'100%', maxHeight:'320px', objectFit:'contain', borderRadius:'8px', background:'#f5f5f5' }}/>
            <button type="button" onClick={() => { setImage(null); setImageBase64(null); setScanned(false); setError('') }}
              style={{ position:'absolute', top:'8px', right:'8px', background:'rgba(0,0,0,0.6)', color:'white', border:'none', borderRadius:'50%', width:'30px', height:'30px', cursor:'pointer', fontSize:'18px', lineHeight:'30px', textAlign:'center' }}>×</button>
          </div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'8px' }}>
            <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'8px', padding:'20px 12px', border:'2px dashed var(--gray-200)', borderRadius:'10px', cursor:'pointer', background:'var(--gray-50)' }}>
              <Camera size={28} style={{ color:'var(--brown-dark)' }}/>
              <span style={{ fontSize:'13px', fontWeight:'500', color:'var(--gray-700)' }}>Take Photo</span>
              <span style={{ fontSize:'11px', color:'var(--gray-400)' }}>Use camera</span>
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" style={{ display:'none' }} onChange={handleImageSelect}/>
            </label>
            <label style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:'8px', padding:'20px 12px', border:'2px dashed var(--gray-200)', borderRadius:'10px', cursor:'pointer', background:'var(--gray-50)' }}>
              <Upload size={28} style={{ color:'var(--gold)' }}/>
              <span style={{ fontSize:'13px', fontWeight:'500', color:'var(--gray-700)' }}>Upload File</span>
              <span style={{ fontSize:'11px', color:'var(--gray-400)' }}>From gallery or files</span>
              <input ref={uploadRef} type="file" accept="image/*" style={{ display:'none' }} onChange={handleImageSelect}/>
            </label>
          </div>
        )}

        {image && !scanned && (
          <button className="btn btn-primary btn-lg" onClick={scanWithClaude} disabled={scanning} style={{ borderRadius:'12px' }}>
            {scanning ? <><RefreshCw size={18} style={{ animation:'spin 1s linear infinite' }}/> Reading with AI…</> : <><Wand2 size={18}/> Extract with AI</>}
          </button>
        )}

        {error && <div style={{ background:'var(--red-light)', color:'var(--red)', padding:'12px 14px', borderRadius:'8px', fontSize:'13px', marginTop:'8px', lineHeight:'1.5' }}>⚠️ {error}</div>}

        {scanned && (
          <div style={{ background:'var(--green-light)', color:'var(--green)', padding:'10px 14px', borderRadius:'8px', fontSize:'13px', marginTop:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
            <Check size={16}/>
            {lineItems.length} garment line{lineItems.length > 1 ? 's' : ''} extracted
            {Object.keys(measurements).length > 0 ? ` + ${Object.keys(measurements).length} measurements` : ''}.
            Review below.
          </div>
        )}
      </div>

      {image && (
        <>
          {/* Order type */}
          <div className="card" style={{ marginBottom:'12px' }}>
            <div className="form-section-title">Order Type</div>
            <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
              <button type="button" style={segBtn(!historical)} onClick={() => setOrderType(false)}>New order</button>
              <button type="button" style={segBtn(historical)} onClick={() => setOrderType(true)}><BookOpen size={14} style={{ verticalAlign:'-2px' }}/> Old order from the books</button>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Order Date</label>
                <input className="form-input" type="date" value={orderDate} max={today()} onChange={e => setOrderDate(e.target.value)}/>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={status} onChange={e => setStatus(e.target.value)}>
                  {historical
                    ? HISTORICAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)
                    : <><option value="measurements_taken">Measurements Taken</option><option value="order_confirmed">Order Confirmed</option></>}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Order No. on Sheet</label>
                <input className="form-input" value={legacyNumber} onChange={e => setLegacyNumber(e.target.value)} placeholder="e.g. 4471"/>
              </div>
              <div className="form-group">
                <label className="form-label">Refers to Orders</label>
                <input className="form-input" value={linkedNumbers} onChange={e => setLinkedNumbers(e.target.value)} placeholder="e.g. 4402, 4398"/>
              </div>
            </div>
            {historical && (
              <div className="form-row">
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">Book</label>
                  <input className="form-input" value={sourceBook} onChange={e => setSourceBook(e.target.value)} placeholder="e.g. 2014 Book A"/>
                </div>
                <div className="form-group" style={{ marginBottom:0 }}>
                  <label className="form-label">Page</label>
                  <input className="form-input" value={sourcePage} onChange={e => setSourcePage(e.target.value)} placeholder="e.g. 37"/>
                </div>
              </div>
            )}
            {historical && <p style={{ fontSize:'11px', color:'var(--gray-400)', marginTop:'8px' }}>Old orders get an H- number and their measurements are filed under the order date, so newer measurements stay on top.</p>}
          </div>

          {/* Client */}
          <div className="card" style={{ marginBottom:'12px' }}>
            <div className="form-section-title">Client on Sheet</div>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-input" value={sheet.name} onChange={e => setSheetField('name', e.target.value)} placeholder="Full name"/>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" type="tel" value={sheet.phone} onChange={e => setSheetField('phone', e.target.value)}/></div>
              <div className="form-group"><label className="form-label">WhatsApp</label><input className="form-input" type="tel" value={sheet.whatsapp} onChange={e => setSheetField('whatsapp', e.target.value)}/></div>
            </div>
            <div className="form-row">
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" value={sheet.email} onChange={e => setSheetField('email', e.target.value)}/></div>
              <div className="form-group"><label className="form-label">City</label><input className="form-input" value={sheet.city} onChange={e => setSheetField('city', e.target.value)}/></div>
            </div>
            <button type="button" className="btn btn-outline btn-sm" style={{ marginBottom:'12px' }} onClick={() => findMatches()} disabled={matching}>
              <Search size={14}/> {matching ? 'Searching…' : 'Find matching clients'}
            </button>

            {lookedUp && (
              <>
                <div style={{ fontSize:'12px', color:'var(--gray-500)', marginBottom:'8px' }}>
                  {candidates.length === 0 ? 'No similar clients found.' : selectedClient ? 'Linked client is highlighted. Tap another to change.' : 'Tap the right client. Phone or email matches are the most reliable.'}
                </div>
                {candidates.map(c => (
                  <CandidateRow key={c.id} c={c} selected={selectedClient?.id === c.id}
                    onPick={pick => setSelectedClient(selectedClient?.id === pick.id ? null : pick)}/>
                ))}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowNewClientPopup(true)}>
                  <UserPlus size={14}/> None of these, create new client
                </button>
              </>
            )}

            <div className="form-group" style={{ marginBottom:0, marginTop:'12px' }}>
              <label className="form-label">Trunk Show City</label>
              <input className="form-input" value={trunkShowCity} onChange={e => setTrunkShowCity(e.target.value)} placeholder="e.g. New York City"/>
            </div>
          </div>

          {/* Line items — all in ONE order */}
          <div className="card" style={{ marginBottom:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
              <div>
                <div className="form-section-title" style={{ marginBottom:0 }}>
                  Items in this Order
                  {scanned && <span style={{ color:'var(--green)', fontSize:'11px', fontWeight:'400' }}> — AI filled</span>}
                </div>
                <div style={{ fontSize:'12px', color:'var(--gray-400)', marginTop:'2px' }}>{orderLabel()}</div>
              </div>
              <button type="button" className="btn btn-outline btn-sm" onClick={addItem}><Plus size={14}/> Add</button>
            </div>

            {lineItems.map((item, i) => (
              <div key={i} style={{ border:'1.5px solid var(--gray-200)', borderRadius:'10px', padding:'14px', marginBottom:'12px', background:'var(--gray-50)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
                  <span style={{ fontSize:'12px', fontWeight:'600', color:'var(--gray-500)', textTransform:'uppercase', letterSpacing:'0.5px' }}>Item {i+1}</span>
                  {lineItems.length > 1 && <button type="button" className="btn btn-danger btn-sm" onClick={() => removeItem(i)}><Trash2 size={13}/></button>}
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Type</label>
                    <select className="form-select" value={item.garment_type} onChange={e => setItem(i,'garment_type',e.target.value)}>
                      {GARMENT_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Qty</label>
                    <input className="form-input" type="number" min="1" value={item.qty} onChange={e => setItem(i,'qty',e.target.value)}/>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <input className="form-input" value={item.description} onChange={e => setItem(i,'description',e.target.value)} placeholder="e.g. slim fit navy trousers"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Style Notes</label>
                  <textarea className="form-textarea" rows={2} value={item.style_notes} onChange={e => setItem(i,'style_notes',e.target.value)} placeholder="Lapel, buttons, pockets, fit…"/>
                </div>
                <div className="form-group">
                  <label className="form-label">Fabric Notes</label>
                  <input className="form-input" value={item.fabric_notes} onChange={e => setItem(i,'fabric_notes',e.target.value)} placeholder="Fabric, color, supplier…"/>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Price <span>(optional)</span></label>
                    <input className="form-input" type="number" step="0.01" value={item.price} onChange={e => setItem(i,'price',e.target.value)} placeholder="0"/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Currency</label>
                    <select className="form-select" value={item.currency} onChange={e => setItem(i,'currency',e.target.value)}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Measurements */}
          <div className="card" style={{ marginBottom:'12px' }}>
            <div className="form-section-title">
              Measurements <span style={{ fontWeight:'400', color:'var(--gray-400)' }}>(inches, saved under {orderDate || 'the order date'})</span>
              {scanned && Object.keys(measurements).length > 0 && <span style={{ color:'var(--green)', fontSize:'11px', fontWeight:'400' }}> — {Object.keys(measurements).length} extracted</span>}
            </div>
            <div className="form-row">
              {MEASUREMENT_FIELDS.map(field => (
                <div className="form-group" key={field.key}>
                  <label className="form-label">{field.label}{measurements[field.key] && <span style={{ color:'var(--green)', marginLeft:'4px' }}>✓</span>}</label>
                  <input className="form-input" type="number" step="0.25" min="0"
                    value={measurements[field.key]||''} onChange={e => setMeasure(field.key,e.target.value)}
                    placeholder="0.00" inputMode="decimal"
                    style={measurements[field.key] ? { borderColor:'var(--green)', background:'#f0fff4' } : {}}/>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="card" style={{ marginBottom:'24px' }}>
            <div className="form-section-title">Notes</div>
            <textarea className="form-textarea" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="Payment notes, delivery instructions, special requests…" style={{ marginBottom:0 }}/>
          </div>

          <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving} style={{ borderRadius:'12px', marginBottom:'8px' }}>
            {saving ? 'Saving…' : `✓ Save ${historical ? 'Old ' : ''}Order (${lineItems.length} item${lineItems.length > 1 ? 's' : ''})${selectedClient ? '' : ' — no client'}`}
          </button>
        </>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  )
}
